import { useMutation, useQueryClient } from '@tanstack/react-query'

import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type { PolicyExtended } from '@/modules/ecom-policy/types'
import { commonQueries } from '@/resources/queries'

import { usePolicyId } from '../hooks'

type ChangePolicySettingsArgs = {
  policyId: number
  payload: Partial<PolicyExtended>
  leasingVendorId?: number | null
  isLeasing?: boolean
}

export const useChangePolicySettings = ({ onError }: { onError: () => void }) => {
  const queryClient = useQueryClient()
  const policyId = usePolicyId()

  const { mutate, isLoading } = useMutation(
    ({ policyId, payload }: ChangePolicySettingsArgs) =>
      ecomPolicyResources.postPolicy({ policyId, payload }),
    {
      async onMutate({ payload: policySettingsPayload }) {
        const queryKey = queries.policyDetails(policyId)

        // Snapshot the previous value
        const previousPolicySettings = queryClient.getQueryData<{
          response: PolicyExtended
        }>(queryKey)

        // Optimistically update to the new value
        queryClient.setQueryData(queryKey, {
          ...previousPolicySettings,
          response: { ...previousPolicySettings?.response, ...policySettingsPayload },
        })

        // Return a context with the previous values
        return previousPolicySettings
      },
      onSuccess(_data, variables) {
        // Set or update leasing vendor
        if (variables.isLeasing && variables.leasingVendorId) {
          updatePolicyLeasingVendor.mutate({
            policyId: policyId,
            vendorId: variables.leasingVendorId,
          })
        }
      },
      // If the mutation fails, use the context we returned above
      onError(_err, _selectProductPayload, previousPolicySettings) {
        onError()
        queryClient.setQueryData(queries.policyDetails(policyId), previousPolicySettings)
      },
      onSettled() {
        queryClient.invalidateQueries(queries.policyDetails(policyId))
        queryClient.invalidateQueries(commonQueries.policyList(), { refetchType: 'all' })
      },
    }
  )

  const updatePolicyLeasingVendor = useMutation(ecomPolicyResources.updatePolicyLeasingVendor, {
    onSuccess: () => {
      queryClient.invalidateQueries(queries.policyDetails(policyId))
      queryClient.invalidateQueries(queries.policyProducts(policyId), { refetchType: 'all' })
      queryClient.invalidateQueries(commonQueries.leasingSettingsByPolicyId(policyId), {
        refetchType: 'all',
      })
    },
  })

  return {
    changePolicySettings: mutate,
    isSaving: isLoading,
    isErrorOnLeasingVendorUpdate: updatePolicyLeasingVendor.isError,
    isLoadingOnLeasingVendorUpdate: updatePolicyLeasingVendor.isLoading,
  }
}
